import { Upload, Plus } from "lucide-react";

/**
 * First-light onboarding card. Shown to a signed-in reader whose library is
 * still empty, in place of the bare "no books yet" state — it points them at
 * the fastest route to a populated Neural Map (importing an existing Goodreads
 * or StoryGraph library) with logging a single book as the lighter alternative.
 *
 * Purely presentational: the two callbacks open the modals the Library page
 * already owns. No data access, so nothing here can affect the shared table.
 */

interface FirstLightCardProps {
  onImport: () => void;
  onLogBook: () => void;
}

// A small decorative constellation so the promise "watch it light up" is shown,
// not just told. Deterministic and purely cosmetic.
const DOTS = [
  { cx: 40, cy: 60, r: 3 }, { cx: 95, cy: 32, r: 2 }, { cx: 150, cy: 70, r: 4 },
  { cx: 205, cy: 40, r: 2.5 }, { cx: 250, cy: 78, r: 3 }, { cx: 120, cy: 105, r: 5 },
  { cx: 185, cy: 120, r: 2.5 }, { cx: 70, cy: 120, r: 2 }, { cx: 230, cy: 120, r: 3 },
];
const LINES = [
  [0, 1], [1, 2], [2, 3], [3, 4], [2, 5], [5, 6], [0, 7], [5, 7], [6, 8], [4, 8],
];

const FirstLightCard = ({ onImport, onLogBook }: FirstLightCardProps) => (
  <div className="relative overflow-hidden rounded-2xl border border-cyan-400/20 bg-gradient-to-b from-slate-900/70 to-slate-950/70 px-6 py-10 text-center">
    {/* Constellation motif */}
    <svg
      viewBox="0 0 290 150"
      className="mx-auto mb-6 w-full max-w-[290px] h-auto"
      aria-hidden="true"
    >
      {LINES.map(([a, b], i) => (
        <line
          key={i}
          x1={DOTS[a].cx}
          y1={DOTS[a].cy}
          x2={DOTS[b].cx}
          y2={DOTS[b].cy}
          stroke="rgba(56,189,248,0.18)"
          strokeWidth="1"
        />
      ))}
      {DOTS.map((d, i) => (
        <circle
          key={i}
          cx={d.cx}
          cy={d.cy}
          r={d.r}
          className="fill-cyan-300 animate-pulse"
          style={{ animationDelay: `${(i % 5) * 0.4}s` }}
        />
      ))}
    </svg>

    <h3 className="text-xl font-semibold text-slate-100 mb-2">Your Neural Map is waiting</h3>
    <p className="text-sm text-slate-400 max-w-md mx-auto mb-6 leading-relaxed">
      Bring your reading history across from Goodreads or The StoryGraph and watch your map light up —
      your books connect themselves by author, theme and era. Or log your first signal by hand.
    </p>

    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
      <button
        onClick={onImport}
        className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-cyan-500/15 border border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/25 hover:border-cyan-400/60 text-sm font-medium transition-colors active:scale-[0.98] touch-manipulation"
      >
        <Upload className="w-4 h-4" />
        Import your library
      </button>
      <button
        onClick={onLogBook}
        className="inline-flex items-center gap-2 h-11 px-5 rounded-lg border border-slate-700/60 text-slate-300 hover:text-slate-100 hover:border-slate-600 text-sm transition-colors active:scale-[0.98] touch-manipulation"
      >
        <Plus className="w-4 h-4" />
        Log a book
      </button>
    </div>
  </div>
);

export default FirstLightCard;
